import type { MemberTrainingPlan, MemberPlanDay } from '@/lib/memberTrainingDay';
import { scheduledPlanUpdate, trainingPlanAtDate } from '@/lib/trainingScheduleHistory';

export const scheduleDate = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
export function isScheduleDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value + 'T12:00:00');
  return Number.isFinite(date.getTime()) && scheduleDate(date) === value;
}
/** Resolve the scheduled version without rewriting storage or an active execution snapshot. */
export function effectiveTrainingSchedule(plan: MemberTrainingPlan, today = scheduleDate()): MemberTrainingPlan {
  const resolved = trainingPlanAtDate(plan, new Date(today + 'T12:00:00'))!;
  const pending = resolved.pendingSchedule;
  if (!pending || !isScheduleDate(pending.effectiveDate) || pending.effectiveDate > today) return resolved;
  const hasVersion = resolved.scheduleHistory?.some(v => v.effectiveFrom >= pending.effectiveDate && v.effectiveFrom <= today);
  return hasVersion ? { ...resolved, pendingSchedule: undefined }
    : { ...resolved, scheduleMode: 'calendar', weekDays: pending.weekDays,
      weeklyHours: pending.weeklyHours, weeklyAvailableHours: pending.weeklyAvailableHours, pendingSchedule: undefined };
}
export function scheduleStamp(plan: MemberTrainingPlan | null): string {
  return JSON.stringify(plan && { id: plan.id, weekDays: plan.weekDays, pendingSchedule: plan.pendingSchedule,
    scheduleMode: plan.scheduleMode, recommendedWeekDays: plan.recommendedWeekDays,
    scheduleHistory: plan.scheduleHistory, weeklyAvailableHours: plan.weeklyAvailableHours });
}
export const copyScheduleDay = (day: MemberPlanDay): MemberPlanDay => structuredClone(day);
export function commitTrainingSchedule(plan: MemberTrainingPlan, days: MemberPlanDay[], effectiveDate: string,
  today: string, makeId: () => string): MemberTrainingPlan {
  if (!isScheduleDate(effectiveDate) || effectiveDate < today) throw new Error('请选择今天或之后的生效日期');
  const active = effectiveTrainingSchedule(plan, today);
  const reference = active.pendingSchedule?.weekDays || active.weekDays;
  const weekDays = days.map(day => {
    const prior = reference.find(d => d.dayOfWeek === day.dayOfWeek);
    const withoutId = (d: MemberPlanDay | undefined) => d && { ...d, virtualId: undefined };
    const same = JSON.stringify(withoutId(prior)) === JSON.stringify(withoutId(day));
    return { ...copyScheduleDay(day), virtualId: same && prior?.virtualId ? prior.virtualId : `member-schedule-${makeId()}` };
  });
  const weeklyHours = Math.round(weekDays.reduce((sum, d) => sum + d.durationMin, 0) / 6) / 10;
  // Historical overrides and records stay intact. New scheduling takes precedence from its effective date.
  const dailyOverrides = Object.fromEntries(Object.entries(active.dailyOverrides || {}).filter(([date]) => date < effectiveDate));
  const replaced = active.pendingSchedule?.effectiveDate;
  const base = { ...active, dailyOverrides, recommendedWeekDays: active.recommendedWeekDays || active.weekDays.map(copyScheduleDay),
    scheduleHistory: active.scheduleHistory?.filter(v => !(replaced && replaced > today && replaced !== effectiveDate && v.effectiveFrom === replaced)) };
  const updated = scheduledPlanUpdate(base, weekDays, effectiveDate, true) as MemberTrainingPlan;
  return effectiveDate > today
    ? { ...base, scheduleHistory: updated.scheduleHistory, pendingSchedule: {
      effectiveDate, weekDays: updated.weekDays, weeklyHours, weeklyAvailableHours: updated.weeklyAvailableHours } }
    : { ...updated, pendingSchedule: undefined };
}
